import gsap from "gsap";
import { Observer } from "gsap/Observer";
import { select, selectAll } from "../../utils";
import { state } from "../state";
import { SliderElements } from "../../types";

/* -------------------------------------------------------------------------- */
/*                                  Constants                                 */
/* -------------------------------------------------------------------------- */

const INTRO_TEXTS = [
  "welcome<br>to the world<br>of tarazum",
  "every guitar has a story<br>every story has a soul",
  "quality<br>beyond<br>the surface",
  "tarazum:<br>onomatopoeia representing<br>the typical sound<br>of a fanfare or band",
  "genuine<br>handcrafted<br>italian guitars",
];

const ANIMATION_CONFIG = {
  standstill: {
    video: 2.5,
    image: 3.0,
  },
  duration: {
    videoTransition: 2.0,
    imageTransition: 2.0,
    introWrapperFade: 1.0,
    overlayFade: 0.3,
  },
  scale: {
    initialX: 5,
    initialY: 1.3,
    transitionX: 1.5,
    transitionY: 1.3,
  },
  easing: {
    main: "expo.inOut",
    headlineOut: "power2.in",
    headlineIn: "power2.out",
  },
};

/* -------------------------------------------------------------------------- */
/*                                    Main                                    */
/* -------------------------------------------------------------------------- */

class IntroSlider {
  private images: HTMLElement[];
  private headline: HTMLElement | null;
  private enterButton: HTMLAnchorElement | null;
  private overlay: HTMLElement | null;

  private currentIndex = 0;
  private animating = false;
  private autoplayCall: gsap.core.Tween | null = null;
  private activeTimeline: gsap.core.Timeline | null = null;
  private wrap: (value: number) => number;
  private observer: Observer | null = null;
  private isEntering = false;

  constructor({ images, headline, enterButton, overlay }: SliderElements) {
    this.images = images;
    this.headline = headline;
    this.enterButton = enterButton;
    this.overlay = overlay;
    this.wrap = gsap.utils.wrap(0, images.length);
  }

  public init(): void {
    this.setupInitialStyles();
    this.setupHeadline();
    this.setupObserver();
    this.setupEnterClick();
    this.scheduleNext();
  }

  private setupInitialStyles(): void {
    this.images.forEach((img, i) => {
      gsap.set(img, {
        xPercent: i === 0 ? 0 : 1,
        scaleX: i === 0 ? 1 : ANIMATION_CONFIG.scale.initialX,
        scaleY: i === 0 ? 1 : ANIMATION_CONFIG.scale.initialY,
        opacity: i === 0 ? 1 : 0,
        zIndex: this.images.length - i,
      });

      if (img instanceof HTMLVideoElement) {
        if (i === 0) {
          img.play().catch(() => {});
        } else {
          img.pause();
        }
      }
    });
  }

  private setupHeadline(): void {
    if (!this.headline) return;
    const firstIsImage = this.images[0]?.tagName === "IMG";
    if (firstIsImage) {
      this.headline.innerHTML = INTRO_TEXTS[0];
      gsap.set(this.headline, { opacity: 1 });
    } else {
      this.headline.innerHTML = "";
      gsap.set(this.headline, { opacity: 0 });
    }
  }

  private scheduleNext(): void {
    if (this.isEntering) return;
    this.killAutoplay();

    const currentItem = this.images[this.currentIndex];
    const isVideo = currentItem?.tagName === "VIDEO";
    const standstillTime = isVideo
      ? ANIMATION_CONFIG.standstill.video
      : ANIMATION_CONFIG.standstill.image;

    this.autoplayCall = gsap.delayedCall(standstillTime, () => {
      if (this.isEntering) return;
      if (!this.animating) {
        this.gotoSection(this.currentIndex + 1, 1);
      } else {
        this.scheduleNext();
      }
    });
  }

  private gotoSection(index: number, direction: number): void {
    if (this.isEntering) return;

    this.killAutoplay();
    if (this.activeTimeline) {
      this.activeTimeline.kill();
      this.activeTimeline = null;
    }

    this.animating = true;

    const targetIndex = this.wrap(index);
    const currentItem = this.images[this.currentIndex];
    const targetItem = this.images[targetIndex];

    const outgoingIsVideo = currentItem?.tagName === "VIDEO";
    const duration = outgoingIsVideo
      ? ANIMATION_CONFIG.duration.videoTransition
      : ANIMATION_CONFIG.duration.imageTransition;

    // Start playing target video if it's a video
    if (targetItem instanceof HTMLVideoElement) {
      targetItem.play().catch(() => {});
    }

    const tl = gsap.timeline({
      defaults: { duration, ease: ANIMATION_CONFIG.easing.main },
      onComplete: () => {
        // Pause outgoing video after transition completes if it's not the same video
        if (
          outgoingIsVideo &&
          currentItem instanceof HTMLVideoElement &&
          currentItem !== targetItem
        ) {
          currentItem.pause();
        }
        this.animating = false;
        this.activeTimeline = null;
        if (!this.isEntering) {
          this.scheduleNext();
        }
      },
    });

    this.activeTimeline = tl;

    // Animate target and current slide images
    tl.fromTo(
      targetItem,
      {
        xPercent: 125 * direction,
        scaleX: ANIMATION_CONFIG.scale.transitionX,
        scaleY: ANIMATION_CONFIG.scale.transitionY,
        opacity: 0,
      },
      { xPercent: 0, scaleX: 1, scaleY: 1, opacity: 1 },
      0,
    ).fromTo(
      currentItem,
      { xPercent: 0, scaleX: 1, scaleY: 1, opacity: 1 },
      {
        xPercent: -125 * direction,
        scaleX: ANIMATION_CONFIG.scale.transitionX,
        scaleY: ANIMATION_CONFIG.scale.transitionY,
        opacity: 0,
      },
      0,
    );

    // Transition the headline content if applicable
    this.transitionHeadline(tl, targetIndex, currentItem, duration);

    this.currentIndex = targetIndex;
  }

  private transitionHeadline(
    tl: gsap.core.Timeline,
    targetIndex: number,
    currentItem: HTMLElement,
    duration: number,
  ): void {
    if (!this.headline) return;

    const incomingIsImage = this.images[targetIndex].tagName === "IMG";
    const outgoingIsImage = currentItem.tagName === "IMG";

    if (outgoingIsImage) {
      const fadeOutDur = duration * 0.2;
      tl.to(
        this.headline,
        {
          duration: fadeOutDur,
          opacity: 0,
          ease: ANIMATION_CONFIG.easing.headlineOut,
        },
        0,
      );
    }

    if (incomingIsImage) {
      const imageOnlyElements = this.images.filter(
        (el) => el.tagName === "IMG",
      );
      const imageIndex = imageOnlyElements.indexOf(this.images[targetIndex]);
      const textToSet = INTRO_TEXTS[imageIndex % INTRO_TEXTS.length];

      const changeTime = duration * 0.3;
      const fadeInDur = duration * 0.5;

      tl.call(
        () => {
          if (this.headline) this.headline.innerHTML = textToSet;
        },
        [],
        changeTime,
      ).to(
        this.headline,
        {
          duration: fadeInDur,
          opacity: 1,
          ease: ANIMATION_CONFIG.easing.headlineIn,
        },
        changeTime,
      );
    }
  }

  private setupObserver(): void {
    this.observer = Observer.create({
      id: "intro-overlay",
      type: "wheel,touch,pointer",
      preventDefault: true,
      wheelSpeed: -1,
      onUp: () => {
        if (!this.animating && !this.isEntering)
          this.gotoSection(this.currentIndex + 1, 1);
      },
      onDown: () => {
        if (!this.animating && !this.isEntering)
          this.gotoSection(this.currentIndex - 1, -1);
      },
      tolerance: 10,
    });
  }

  private setupEnterClick(): void {
    if (!this.enterButton) return;
    this.enterButton.onclick = (e) => {
      e.preventDefault();
      if (this.isEntering) return;
      this.isEntering = true;

      // 1. Disable and kill observers
      if (this.observer) {
        this.observer.kill();
        this.observer = null;
      }
      const introObs = Observer.getById("intro-overlay");
      if (introObs) introObs.kill();

      // 2. Kill GSAP autoplay & active transition timeline
      this.killAutoplay();
      if (this.activeTimeline) {
        this.activeTimeline.kill();
        this.activeTimeline = null;
      }

      // 3. Immediately pause all video elements
      this.pauseAllVideos();

      // 4. Clean up enter click listener
      if (this.enterButton) {
        this.enterButton.onclick = null;
      }

      // 5. Fade out overlay, set display: none, and destroy
      if (this.overlay) {
        gsap.to(this.overlay, {
          duration: ANIMATION_CONFIG.duration.overlayFade,
          opacity: 0,
          onComplete: () => {
            if (this.overlay) {
              this.overlay.style.pointerEvents = "none";
              this.overlay.style.display = "none";
            }
            this.destroy();
          },
        });
      } else {
        this.destroy();
      }
    };
  }

  private pauseAllVideos(): void {
    this.images.forEach((img) => {
      if (img instanceof HTMLVideoElement) {
        img.pause();
        img.currentTime = 0;
      }
    });
  }

  private killAutoplay(): void {
    if (this.autoplayCall) {
      this.autoplayCall.kill();
      this.autoplayCall = null;
    }
  }

  private destroy(): void {
    if (this.observer) {
      this.observer.kill();
      this.observer = null;
    }
    this.killAutoplay();
    if (this.activeTimeline) {
      this.activeTimeline.kill();
      this.activeTimeline = null;
    }
    if (this.enterButton) {
      this.enterButton.onclick = null;
      this.enterButton = null;
    }
    this.pauseAllVideos();
    this.headline = null;
    this.overlay = null;
    this.images = [];
  }
}

export const initLoader = (): void => {
  state.loader.start();
  state.loader.end();
  state.loader.animation.eventCallback("onComplete", () => {
    state.loader.hide();

    const introWrapper = select(".intro__wrapper");
    const enter = select(".intro__enter") as HTMLAnchorElement | null;
    const overlay = select(".overlay__loader");

    if (!introWrapper || !enter || !overlay) return;

    gsap.to(introWrapper, {
      duration: ANIMATION_CONFIG.duration.introWrapperFade,
      display: "flex",
      opacity: 1,
    });

    const introImages = Array.from(
      selectAll(".intro__images__wrapper .intro__img"),
    ) as HTMLElement[];

    if (introImages.length > 0) {
      const introHeadline = select(".intro__headline");
      const slider = new IntroSlider({
        images: introImages,
        headline: introHeadline,
        enterButton: enter,
        overlay: overlay,
      });
      slider.init();
    }
  });
};
